<template>
  <nav class="navbar navbar--main">
    <div class="collapse navbar-collapse" id="navbarNav">
      <ul class="navbar-nav navbar-nav--lv1">

        <!-- Main Menu Items -->
        <template v-if="mainMenus">

          <li v-for="(item, index) in mainMenus?.data" :key="index" :class="['nav-item', { dropdown: item.children?.length }]">

            <NuxtLink v-if="!item.children?.length" :to="localePath(item.url)" class="nav-link" :target="item.target"
              @click="closeMainDropdown">
              {{ item.name }}
            </NuxtLink>

            <template v-else>
              <a class="nav-link dropdown-toggle" href="javascript:void(0);" role="button" data-bs-toggle="dropdown"
                data-bs-auto-close="outside">
                {{ item.name }}
              </a>

              <div class="dropdown-menu dropdown-menu--wrapper">
                <ul class="dropdown-menu dropdown-menu--lv2">
                  <li v-for="(subItem, subIndex) in item.children" :key="subIndex"
                    :class="['dropdown-item', { dropdown: subItem.children?.length }]">

                    <!-- Level 2 items without children -->
                    <NuxtLink v-if="!subItem.children?.length" class="dropdown-link" :to="localePath(subItem.url)"
                      :target="subItem.target" @click="closeMainDropdown">
                      {{ subItem.name }}
                    </NuxtLink>

                    <!-- Level 2 items with children (Level 3 dropdown) -->
                    <template v-else>

                      <a class="dropdown-link dropdown-toggle" href="javascript:void(0);" data-bs-toggle="dropdown"
                        data-bs-auto-close="outside">
                        {{ subItem.name }} 
                      </a>

                      <ul class="dropdown-menu dropdown-menu--lv3">
                        <li v-for="(level3Item, level3Index) in subItem.children" :key="level3Index"
                          class="dropdown-item">
                          <NuxtLink class="dropdown-link" :to="localePath(level3Item.url)" :target="level3Item.target"
                            @click="closeMainDropdown">
                            {{ level3Item.name }}
                          </NuxtLink>
                        </li>
                      </ul>
                    </template>
                  </li>
                </ul>
              </div>
            </template>
          </li>
        </template>

      </ul>
    </div>
  </nav>
</template>

<script setup>

import { useMenuServiceStore } from '@/stores/menuService.js';

const menuStore = useMenuServiceStore();
const { fetchMenu } = menuStore;

// ดึง footer menu
const menus = await fetchMenu("/main");
const mainMenus = menus['/main'];

const emit = defineEmits(['link-clicked']);

const closeMainDropdown = () => {
  emit('link-clicked');
  const mainDropdownToggles = document.querySelectorAll('.navbar-nav--lv1 .nav-link.dropdown-toggle.show');
  mainDropdownToggles.forEach(toggle => {
    toggle.classList.remove('show');
    toggle.setAttribute('aria-expanded', 'false');

    const dropdownWrapper = toggle.nextElementSibling;
    if (dropdownWrapper && dropdownWrapper.classList.contains('dropdown-menu--wrapper')) {
      dropdownWrapper.classList.remove('show');

      const level3Menus = dropdownWrapper.querySelectorAll('.dropdown-menu.dropdown-menu--lv3.show');
      level3Menus.forEach(menu => {
        menu.classList.remove('show');
      });
    }

  });
}

</script>

<style lang="scss" scoped>
@import "@/assets/scss/app";

@each $lang in $languages {
  html[lang="#{$lang}"] {
    .navbar-nav {
      &.navbar-nav--lv1 {
        .nav-item {
          .nav-link {
            @include font-size("h4", $lang);
            @include font-weight("medium", $lang);
          }
        }
      }
    }

    .dropdown-menu {

      &.dropdown-menu--lv2,
      &.dropdown-menu--lv3 {
        .dropdown-item {
          .dropdown-link {
            @include font-size("h4", $lang);
            @include font-weight("medium", $lang);
          }
        }
      }
    }
  }
}

.navbar {
  &.navbar--main {
    position: unset;

    @include media-breakpoint-down(lg) {
      padding: 0;
    }

    .navbar-collapse {
      @include media-breakpoint-up(lg) {
        height: 100%;
      }

      &:not(.show) {
        @include media-breakpoint-up(xl) {
          display: block;
        }
      }
    }
  }
}

.navbar-nav {
  &.navbar-nav--lv1 {
    flex-direction: row;
    justify-content: space-between;
    height: 100%;

    .nav-item {
      position: relative;

      .nav-link {
        position: relative;
        width: fit-content;
        color: $white;
        padding: 8px 12px;
        text-transform: uppercase;
        border-radius: 12px 12px 0px 0px;

        @include hover-focus-active {
          color: $green-2;
        }


        &.dropdown-toggle {
          &::before {
            position: absolute;
            content: "";
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
            z-index: -1;
            background: rgba($lightgreen-1, 0.90);
            backdrop-filter: blur(2.5px);
            border-radius: 12px 12px 0px 0px;
            opacity: 0;
          }

          &::after {
            border: 0;
            margin: 0;
          }

          @include media-breakpoint-down(lg) {
            &::after {
              color: $black;
              @include font-fontawesome('\f054');
              position: relative;
              font-size: 11px;
              font-weight: 500;
              top: -2px;
              padding-left: 0.75rem;
            }
          }

          &.show {
            color: $green-2;
            box-shadow: none;
            @include transition();

            &::before {
              opacity: 1;
            }

              &::after {
                 color: $green-2;
              }
          }
        }
      }

    }
  }
}

.dropdown-menu {
  &.dropdown-menu--wrapper {
    position: absolute;
    top: 100%;
    left: 0;
    padding: 12px;
    border: 0;
    min-width: 200px;
    transition: width 0.3s ease, height 0.3s ease;

    &::before {
      position: absolute;
      content: "";
      width: 100%;
      height: 100%;
      left: 0;
      top: -3px;
      z-index: -1;
      background: rgba($lightgreen-1, 0.90);
      backdrop-filter: blur(2.5px);
      border-radius: 0px 12px 12px 12px;
      transition: width 0.3s ease, height 0.3s ease;
    }

    @include media-breakpoint-down(lg) {
      position: relative;
      min-width: auto;
      padding: 8px 12px;

      &::before {
        content: none;
      }
    }


    .dropdown-item {
      position: static;
      padding: 0;
      white-space: wrap;

      @include hover-focus-active {
        background-color: transparent;
      }

    }
  }

  &.dropdown-menu--lv2 {
    position: relative;
    display: block;
    max-width: 176px;

    @include media-breakpoint-down(lg) {
      max-width: 100%;
    }

    .dropdown-item {

      &:first-child {
        .dropdown-link {
          padding-top: 0;
        }
      }

      @include media-breakpoint-down(xl) {
        display: block;
      }
    }

    .dropdown-link {
      position: relative;
      display: block;
      width: 100%;
      padding: 5px 0;
      white-space: wrap;

      &.router-link-active {
        color: $green-2;
      }


      &.dropdown-toggle {
        &::after {
          color: $black;
          @include font-fontawesome('\f054');
          position: relative;
          font-size: 11px;
          font-weight: 500;
          top: -1px;
          padding-left: 0.75rem;
        }
      }


      @include hover-focus-active {
        background-color: transparent;
        color: $green-2;

        &::after {
          color: $green-2;
        }
      }
    }

  }

  &.dropdown-menu--lv3 {
    position: absolute;
    left: 174px;
    top: -12px;
    min-width: 301px;
    padding: 12px;
    @include transition();

    @include media-breakpoint-down(lg) {
      position: relative;
      left: 0;
      top: 0;
      min-width: auto;
      padding: 8px 12px 0;
    }


    .dropdown-link {
      position: relative;
      padding-left: 1.75rem;

      &.router-link-active {
        color: $green-2;
      }

      &::before {
        position: absolute;
        content: "";
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: $black;
        left: 10px;
        top: 11px;
        bottom: 0;
      }
    }

    &.show {
      animation: fadeIn .7s;
    }

  }

}


@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
